import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaUserShield, FaLock, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  max-width: 450px;
  margin: 0 auto;
  min-height: 80vh;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  
  svg {
    font-size: 2.2rem;
    margin-right: 0.75rem;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  padding: 2rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const StyledForm = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
  transition: all 0.3s ease;
  
  &:focus-within {
    transform: translateY(-2px);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.card || '#242832'};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px ${props => props.theme.colors.primary}25;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textMuted || 'rgba(255, 255, 255, 0.5)'};
    transition: opacity 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.5;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  transition: all 0.3s ease;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: ${props => props.theme.colors.error}15;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  border-left: 4px solid ${props => props.theme.colors.error};
  animation: ${fadeIn} 0.3s ease-out;
  
  svg {
    color: ${props => props.theme.colors.error};
    flex-shrink: 0;
  }
`;

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoadingIcon = styled.span`
  .spinner {
    animation: ${spinAnimation} 1s linear infinite;
    display: inline-block;
  }
`;

const ButtonWithSpinner = styled(Button)`
  width: 100%;
  padding: 0.9rem;
  font-size: 1rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary || props.theme.colors.primary});
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  & > svg {
    margin-right: 0.5rem;
  }
  
  & > .spinner {
    animation: ${spinAnimation} 1s linear infinite;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await login(credentials);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Identifiants invalides. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Logo>
        <FaUserShield />
        Portail Admin
      </Logo>
      
      <StyledCard>
        <Title>Connexion Administrateur</Title>
        
        {error && (
          <ErrorMessage>
            <FaExclamationTriangle />
            {error}
          </ErrorMessage>
        )}
        
        <StyledForm onSubmit={handleSubmit}>
          <FormGroup>
            <InputIcon>
              <FaUserShield />
            </InputIcon>
            <Input 
              type="text"
              name="username"
              placeholder="Nom d'utilisateur"
              value={credentials.username}
              onChange={handleChange}
              required
              autoComplete="username"
            />
          </FormGroup>
          
          <FormGroup>
            <InputIcon>
              <FaLock />
            </InputIcon>
            <Input 
              type="password"
              name="password"
              placeholder="Mot de passe"
              value={credentials.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </FormGroup>
          
          <ButtonWithSpinner type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <LoadingIcon>
                  <FaSpinner className="spinner" />
                </LoadingIcon>
                Connexion en cours...
              </>
            ) : (
              <>
                <FaUserShield />
                Se connecter
              </>
            )}
          </ButtonWithSpinner>
        </StyledForm>
      </StyledCard>
    </PageContainer>
  );
};

export default AdminLogin; 